import React, { useEffect, useMemo, useState } from 'react';
import { Input, Select } from '../../../components/ui/Input';
import Btn from '../../../components/ui/Btn';
import Secao from '../../../components/ui/Secao';
import Badge from '../../../components/ui/Badge';
import { usuariosApi } from '../../../api/admin';
import { listarTelasDisponiveis, telasPadraoDoPerfil } from '../../../layout/Sidebar';
import { COLORS } from '../../../theme';

const PERFIS = [
  { value: 'gestor', label: 'Gestor (acesso a tudo)' },
  { value: 'vendedor', label: 'Vendedor' },
  { value: 'comprador', label: 'Comprador' },
  { value: 'estoquista', label: 'Estoquista' }
];

const TODAS_TELAS = listarTelasDisponiveis();
const GRUPOS = Array.from(new Set(TODAS_TELAS.map(t => t.grupoTitulo)));

function sugerirLogin(nome) {
  if (!nome) return '';
  const partes = nome.trim().toLowerCase().split(/\s+/);
  const base = partes.length > 1 ? `${partes[0]}.${partes[partes.length - 1]}` : partes[0];
  const semAcento = base.normalize('NFD').replace(/[̀-ͯ]/g, '');
  return semAcento.replace(/[^a-z0-9.]/g, '');
}

function parseTelasPermitidas(valor) {
  if (!valor) return null;
  try {
    const parsed = typeof valor === 'string' ? JSON.parse(valor) : valor;
    return Array.isArray(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
}

export default function AcessoTab({ funcionarioId, nomeSugerido }) {
  const [usuario, setUsuario] = useState(undefined);
  const [form, setForm] = useState({ email: '', senha: '', perfil: 'vendedor', autorizado_tabela_completa: false });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [ok, setOk] = useState('');

  const [selecionadas, setSelecionadas] = useState(new Set());
  const [personalizado, setPersonalizado] = useState(false);
  const [salvandoPerm, setSalvandoPerm] = useState(false);
  const [okPerm, setOkPerm] = useState('');

  const carregar = () => {
    usuariosApi.listar({ funcionario_id: funcionarioId }).then(lista => setUsuario(lista[0] || null)).catch(() => setUsuario(null));
  };

  useEffect(() => {
    if (!funcionarioId) return;
    carregar();
  }, [funcionarioId]); // eslint-disable-line

  useEffect(() => {
    if (usuario === null) setForm(f => ({ ...f, email: `${sugerirLogin(nomeSugerido)}@coren.local` }));
    if (usuario) setForm({ email: usuario.email, senha: '', perfil: usuario.perfil, autorizado_tabela_completa: !!usuario.autorizado_tabela_completa });
  }, [usuario, nomeSugerido]);

  useEffect(() => {
    if (!usuario) { setSelecionadas(new Set()); setPersonalizado(false); return; }
    const custom = parseTelasPermitidas(usuario.telas_permitidas);
    if (custom) {
      setSelecionadas(new Set(custom));
      setPersonalizado(true);
    } else {
      setSelecionadas(new Set(telasPadraoDoPerfil(usuario.perfil)));
      setPersonalizado(false);
    }
    setOkPerm('');
  }, [usuario]);

  const telasVisiveisParaPerfil = useMemo(() => {
    if (!usuario) return [];
    return TODAS_TELAS.filter(t => !t.perfis || t.perfis.includes(usuario.perfil));
  }, [usuario]);

  if (!funcionarioId) return <p style={{ color: COLORS.textMid, fontSize: 14 }}>Salve o cadastro na aba Dados Pessoais antes de criar um acesso.</p>;
  if (usuario === undefined) return null;

  const onChange = (campo, valor) => setForm(f => ({ ...f, [campo]: valor }));

  const salvar = async () => {
    setSalvando(true);
    setErro(''); setOk('');
    try {
      if (usuario) {
        const dados = { email: form.email, perfil: form.perfil, autorizado_tabela_completa: form.autorizado_tabela_completa ? 1 : 0 };
        if (form.senha) dados.senha = form.senha;
        await usuariosApi.atualizar(usuario.id, dados);
        setOk('Acesso atualizado.');
      } else {
        if (!form.senha) { setErro('Defina uma senha para criar o acesso'); setSalvando(false); return; }
        await usuariosApi.criar({ nome: nomeSugerido, email: form.email, senha: form.senha, perfil: form.perfil, funcionario_id: funcionarioId });
        setOk('Acesso criado com sucesso.');
      }
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const alternarTela = (chave) => {
    setSelecionadas(sel => {
      const nova = new Set(sel);
      if (nova.has(chave)) nova.delete(chave); else nova.add(chave);
      return nova;
    });
  };

  const salvarPermissoes = async () => {
    setSalvandoPerm(true);
    setOkPerm('');
    try {
      await usuariosApi.atualizar(usuario.id, { telas_permitidas: Array.from(selecionadas) });
      setPersonalizado(true);
      setOkPerm('Permissões salvas.');
      carregar();
    } finally {
      setSalvandoPerm(false);
    }
  };

  const restaurarPadrao = async () => {
    setSalvandoPerm(true);
    setOkPerm('');
    try {
      await usuariosApi.atualizar(usuario.id, { telas_permitidas: null });
      setSelecionadas(new Set(telasPadraoDoPerfil(usuario.perfil)));
      setPersonalizado(false);
      setOkPerm('Restaurado para o padrão do perfil.');
      carregar();
    } finally {
      setSalvandoPerm(false);
    }
  };

  return (
    <>
      <Secao titulo="Acesso ao Sistema" acao={usuario ? <Badge tom={usuario.ativo ? 'sucesso' : 'perigo'}>{usuario.ativo ? 'Ativo' : 'Inativo'}</Badge> : null}>
        <p style={{ fontSize: 13, color: COLORS.textMid, marginTop: 0 }}>
          {usuario ? 'Este funcionário já tem login no sistema. Deixe a senha em branco pra manter a atual.' : 'Opcional — só preencha se este funcionário precisa logar no sistema.'}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 6, maxWidth: 620 }}>
          <Input label="Email de Login" type="email" value={form.email} onChange={e => onChange('email', e.target.value)} />
          <Input label={usuario ? 'Nova Senha' : 'Senha'} type="password" value={form.senha} onChange={e => onChange('senha', e.target.value)} />
          <Select label="Perfil" options={PERFIS} value={form.perfil} onChange={e => onChange('perfil', e.target.value)} />
        </div>

        {form.perfil === 'vendedor' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 12 }}>
            <input type="checkbox" checked={!!form.autorizado_tabela_completa} onChange={e => onChange('autorizado_tabela_completa', e.target.checked)} />
            Autorizado a vender abaixo do preço de Promoção (acesso a todas as tabelas de preço)
          </label>
        )}

        {erro && <p style={{ color: COLORS.danger, fontSize: 13 }}>{erro}</p>}
        {ok && <p style={{ color: '#1a9c5c', fontSize: 13 }}>{ok}</p>}
        <Btn onClick={salvar} disabled={salvando} style={{ marginTop: 10 }}>
          {salvando ? 'Salvando...' : usuario ? 'Atualizar Acesso' : 'Criar Acesso'}
        </Btn>
      </Secao>

      {usuario && (
        <Secao
          titulo="Permissões de Telas"
          acao={<span style={{ fontSize: 12, color: COLORS.textMid }}>{personalizado ? 'Permissões personalizadas' : 'Usando padrão do perfil'}</span>}
        >
          <p style={{ fontSize: 13, color: COLORS.textMid, marginTop: 0 }}>
            Por padrão, este usuário vê as telas liberadas pro perfil dele. Aqui dá pra restringir ou liberar
            telas específicas pra ele, sem mudar o perfil. Isso não afeta o que o perfil já bloqueia por trás.
          </p>

          {telasVisiveisParaPerfil.length === 0 ? (
            <p style={{ fontSize: 13, color: COLORS.textMid }}>O perfil "{usuario.perfil}" não tem nenhuma tela liberada por padrão.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20 }}>
              {GRUPOS.map(grupo => {
                const telasDoGrupo = telasVisiveisParaPerfil.filter(t => t.grupoTitulo === grupo);
                if (telasDoGrupo.length === 0) return null;
                return (
                  <div key={grupo}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMid, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                      {grupo}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {telasDoGrupo.map(t => (
                        <label key={t.chave} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                          <input type="checkbox" checked={selecionadas.has(t.chave)} onChange={() => alternarTela(t.chave)} />
                          {t.titulo}
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {okPerm && <p style={{ color: '#1a9c5c', fontSize: 13 }}>{okPerm}</p>}
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            {personalizado && <Btn variante="ghost" onClick={restaurarPadrao} disabled={salvandoPerm}>Restaurar Padrão do Perfil</Btn>}
            <Btn onClick={salvarPermissoes} disabled={salvandoPerm}>{salvandoPerm ? 'Salvando...' : 'Salvar Permissões'}</Btn>
          </div>
        </Secao>
      )}
    </>
  );
}
